import { EventTypes } from '@racemap/utilities/consts/events';
import { isEmptyString } from '@racemap/utilities/functions/utils';
import { validateRREventId, validateUrl } from '@racemap/utilities/functions/validation';
import { type GroupEvent, RRImportMode, type RacemapEvent } from '@racemap/utilities/types/events';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { Link } from 'react-router-dom';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useCurrentParentEvent } from 'src/lib/customHooks/useCurrentParentEvent';
import { CheckList } from '../../BasicComponents/CheckList';
import { HStack, VStack } from '../../BasicComponents/MetaComponent';
import { Checkpoint, type CheckpointEntry } from '../PredictionChecklist';
import { CustomerIdsConfiguration } from './CustomerIdsConfiguration';

export const RaceresultConfiguration: FC = () => {
  const event = useCurrentEvent();
  const parent = useCurrentParentEvent();

  if (event == null) return <></>;

  const customerIds = event.integrations.raceresult.customerIds || [];

  const checkpoints: Array<CheckpointEntry> = [
    getCustomerIdsCheckpoint(customerIds),
    getParticipantImportCheckpoint(event, parent),
  ];

  return (
    <VStack>
      <CheckList checkpoints={checkpoints} />
    </VStack>
  );
};

function getCustomerIdsCheckpoint(customerIds: Immutable<Array<string>>): CheckpointEntry {
  const status = getCustomerIdsCheckStatus(customerIds);

  return {
    status,
    key: 'customerIds',
    checkpoint: (
      <Checkpoint>
        <HStack gap="5px" align="left">
          <CustomerIdsConfiguration />
        </HStack>
      </Checkpoint>
    ),
  };
}

function getParticipantImportCheckpoint(
  event: Immutable<RacemapEvent>,
  parent: Immutable<GroupEvent> | null,
): CheckpointEntry {
  let rrIntegration;
  if (event.type !== EventTypes.STAGE) {
    rrIntegration = event.integrations.raceresult;
  } else {
    rrIntegration = parent?.integrations.raceresult;
  }

  if (rrIntegration == null)
    return {
      status: 'danger',
      key: 'import',
      checkpoint: <Checkpoint title="Import participants and transponders">Unknown</Checkpoint>,
    };

  const importMode = rrIntegration.importMode;
  const contestAdded = rrIntegration.contests.length > 0;

  if (!contestAdded) {
    return {
      status: 'danger',
      key: 'import',
      checkpoint: (
        <Checkpoint title=" Import participants and transponders">
          You have to define the contest, that is for that event relevant!
        </Checkpoint>
      ),
    };
  }

  switch (importMode) {
    case RRImportMode.CUSTOM_URL:
      if (!validateUrl(rrIntegration.starterListUrl)) {
        return {
          status: 'danger',
          key: 'import',
          checkpoint: (
            <Checkpoint title="Import participants and transponders">
              Your saved import url is invalid!
            </Checkpoint>
          ),
        };
      }
      return {
        status: 'success',
        key: 'import',
        checkpoint: (
          <Checkpoint title="Import participants and transponders">
            The import url is valid.
          </Checkpoint>
        ),
      };
    case RRImportMode.EVENT_ID:
      if (isEmptyString(rrIntegration.eventId)) {
        return {
          status: 'danger',
          key: 'import',
          checkpoint: (
            <Checkpoint title="Import participants and transponders">
              Provide the event id to synchronize your participants&apos; data from RACE RESULT. Add
              the event id in the{' '}
              <Link to={`/admin/event/${event.id}/participants/import_export`}>
                participant import
              </Link>
              .
            </Checkpoint>
          ),
        };
      }
      if (!validateRREventId(rrIntegration.eventId)) {
        return {
          status: 'danger',
          key: 'import',
          checkpoint: (
            <Checkpoint title="Import participants and transponders">
              The event id {rrIntegration.eventId} is not valid. You can change the event id in the{' '}
              <Link to={`/admin/event/${event.id}/participants/import_export`}>
                participant import
              </Link>
              .
            </Checkpoint>
          ),
        };
      }
      return {
        status: 'success',
        key: 'import',
        checkpoint: (
          <Checkpoint title="Import participants and transponders">
            The event id {rrIntegration.eventId} is valid. You can change the event id in the{' '}
            <Link to={`/admin/event/${event.id}/participants/import_export`}>
              participant import
            </Link>
          </Checkpoint>
        ),
      };
  }
}

function getCustomerIdsCheckStatus(
  customerIds: Immutable<Array<string>>,
): CheckpointEntry['status'] {
  if (customerIds == null || customerIds.length === 0) return 'danger';
  if (customerIds.some((id) => id === '')) return 'secondary';
  return 'success';
}
